import { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../../api/axiosInstance";

const incomeApi = {
    list: (vid, q = "") =>
        axiosInstance.get(`/api/superadmin/vendors/${vid}/income-documents/?q=${encodeURIComponent(q)}`),
    get: (vid, did) =>
        axiosInstance.get(`/api/superadmin/vendors/${vid}/income-documents/${did}/`),
    create: (vid, fd) =>
        axiosInstance.post(`/api/superadmin/vendors/${vid}/income-documents/`, fd),
    patch: (vid, did, body) =>
        axiosInstance.patch(`/api/superadmin/vendors/${vid}/income-documents/${did}/`, body),
    delete: (vid, did) =>
        axiosInstance.delete(`/api/superadmin/vendors/${vid}/income-documents/${did}/`),
};


const PROOF_TYPES = [
    { value: "itr", label: "ITR (3 years)", tw: { pill: "bg-blue-50 text-blue-700 ring-blue-200", icon: "bg-blue-100 text-blue-600" } },
    { value: "work_order", label: "Work Order", tw: { pill: "bg-amber-50 text-amber-700 ring-amber-200", icon: "bg-amber-100 text-amber-600" } },
    { value: "balance_sheet", label: "Audited Balance Sheet", tw: { pill: "bg-slate-50 text-slate-600 ring-slate-200", icon: "bg-slate-100 text-slate-500" } },
];
const getProofCfg = (v) => PROOF_TYPES.find((p) => p.value === v) || PROOF_TYPES[0];

const DOC_INDICES = [1, 2, 3];

const FY_OPTIONS = (() => {
    const cur = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => {
        const y = cur - i;
        return { value: `${y - 1}-${y}`, label: `FY ${y - 1}–${y}` };
    });
})();

const formatINR = (v) => {
    if (!v && v !== 0) return "—";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(parseFloat(v));
};
const formatBytes = (b) => {
    if (!b) return "—";
    return b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;
};


const TOAST_MS = 4000;
let _toastSet = null;
const toast = {
    _push: (item) => { const id = Date.now() + Math.random(); _toastSet?.((p) => [...p, { id, ...item }]); return id; },
    success: (t, m) => toast._push({ type: "success", title: t, msg: m }),
    error: (t, m) => toast._push({ type: "error", title: t, msg: m }),
    info: (t, m) => toast._push({ type: "info", title: t, msg: m }),
    loading: (t) => toast._push({ type: "loading", title: t, persistent: true }),
    dismiss: (id) => _toastSet?.((p) => p.filter((x) => x.id !== id)),
};
const TOAST_META = {
    success: { bar: "#16a34a", icon: "✓" },
    error: { bar: "#dc2626", icon: "✕" },
    info: { bar: "#2563eb", icon: "i" },
    loading: { bar: "#94a3b8" },
};
function ToastItem({ t, onDismiss }) {
    const m = TOAST_META[t.type];
    const [prog, setProg] = useState(100);
    const [exit, setExit] = useState(false);
    const raf = useRef(null); const ts = useRef(Date.now());
    const dismiss = useCallback(() => { setExit(true); setTimeout(() => onDismiss(t.id), 260); }, [t.id, onDismiss]);
    useEffect(() => {
        if (t.persistent) return;
        const tick = () => { const p = Math.max(0, 100 - ((Date.now() - ts.current) / TOAST_MS) * 100); setProg(p); if (p > 0) raf.current = requestAnimationFrame(tick); else dismiss(); };
        raf.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf.current);
    }, [t.persistent, dismiss]);
    return (
        <div className={`relative overflow-hidden bg-white rounded-xl shadow-xl min-w-[280px] max-w-[340px] ${exit ? "animate-[slideOut_.26s_ease_forwards]" : "animate-[slideIn_.28s_cubic-bezier(.34,1.56,.64,1)_forwards]"}`}
            style={{ borderLeft: `4px solid ${m.bar}` }}>
            <div className="flex items-start gap-3 p-3 pb-3.5">
                <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: `${m.bar}20` }}>
                    {t.type === "loading"
                        ? <svg className="w-3 h-3 animate-spin" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="5" stroke={m.bar} strokeWidth="2" strokeDasharray="8 20" strokeLinecap="round" /></svg>
                        : <span className="text-[10px] font-black" style={{ color: m.bar }}>{m.icon}</span>}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="m-0 text-[13px] font-semibold text-slate-900 leading-tight">{t.title}</p>
                    {t.msg && <p className="m-0 mt-0.5 text-[12px] text-slate-500 leading-snug">{t.msg}</p>}
                </div>
                <button onClick={dismiss} className="text-slate-300 hover:text-slate-500 p-0.5 shrink-0 transition-colors">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
            </div>
            {!t.persistent && (
                <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-slate-100">
                    <div className="h-full transition-[width_.1s_linear] rounded-sm" style={{ width: `${prog}%`, background: m.bar }} />
                </div>
            )}
        </div>
    );
}
function ToastContainer() {
    const [toasts, setToasts] = useState([]); _toastSet = setToasts;
    const dismiss = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
    if (!toasts.length) return null;
    return (
        <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-2 items-end">
            {toasts.map((t) => <ToastItem key={t.id} t={t} onDismiss={dismiss} />)}
        </div>
    );
}


function Spinner({ className = "w-4 h-4" }) {
    return <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>;
}

function Btn({ children, onClick, variant = "primary", size = "sm", disabled, loading: isLoading, icon, className = "" }) {
    const base = "inline-flex items-center gap-1.5 font-semibold rounded-lg transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap";
    const pad = size === "sm" ? "px-3.5 py-1.5 text-[13px]" : "px-5 py-2.5 text-sm";
    const vars = {
        primary: "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm",
        secondary: "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50",
        danger: "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100",
        ghost: "bg-transparent text-slate-500 hover:bg-slate-100",
        outline: "bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-50",
    };
    return (
        <button onClick={onClick} disabled={disabled || isLoading} className={`${base} ${pad} ${vars[variant]} ${className}`}>
            {isLoading ? <Spinner className="w-3.5 h-3.5" /> : icon}
            {children}
        </button>
    );
}

function FieldError({ msg }) {
    if (!msg) return null;
    return <p className="mt-1 text-[12px] text-red-600 flex items-center gap-1"><span>⚠</span>{msg}</p>;
}

function Label({ children, required, hint }) {
    return (
        <label className="block text-[13px] font-semibold text-slate-700 mb-1.5">
            {children}{required && <span className="text-red-500 ml-0.5">*</span>}
            {hint && <span className="ml-2 text-[11px] font-normal text-slate-400">{hint}</span>}
        </label>
    );
}

function SkeletonCard() {
    return (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
            <div className="h-36 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 animate-pulse" />
            <div className="p-4 space-y-2.5">
                <div className="h-4 w-2/3 rounded-md bg-slate-100 animate-pulse" />
                <div className="h-3 w-1/2 rounded-md bg-slate-100 animate-pulse" />
                <div className="h-3 w-3/4 rounded-md bg-slate-100 animate-pulse" />
                <div className="flex gap-2 mt-3">
                    <div className="h-8 flex-1 rounded-lg bg-slate-100 animate-pulse" />
                    <div className="h-8 w-8 rounded-lg bg-slate-100 animate-pulse" />
                    <div className="h-8 w-8 rounded-lg bg-slate-100 animate-pulse" />
                </div>
            </div>
        </div>
    );
}


function Modal({ title, sub, onClose, children, width = "max-w-xl" }) {
    useEffect(() => {
        const h = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", h);
        return () => document.removeEventListener("keydown", h);
    }, [onClose]);
    return (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-[2px] flex items-center justify-center z-[2000] p-4"
            onClick={onClose}>
            <div className={`bg-white rounded-2xl w-full ${width} shadow-2xl animate-[fadeUp_.2s_ease]`}
                onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start px-6 pt-5 pb-0">
                    <div>
                        <h3 className="text-[16px] font-bold text-slate-900 leading-tight">{title}</h3>
                        {sub && <p className="text-[12px] text-slate-400 mt-0.5">{sub}</p>}
                    </div>
                    <button onClick={onClose}
                        className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                </div>
                <div className="px-6 py-5 max-h-[82vh] overflow-y-auto">{children}</div>
            </div>
        </div>
    );
}


function ConfirmDelete({ docName, onConfirm, onCancel, busy }) {
    return (
        <Modal title="Delete Income Document" onClose={onCancel} width="max-w-sm">
            <div className="text-center pt-2">
                <div className="w-14 h-14 rounded-full bg-red-50 border-2 border-red-100 flex items-center justify-center mx-auto mb-4 text-2xl">🗑️</div>
                <p className="text-[15px] font-bold text-slate-900 mb-2">Remove this document?</p>
                <p className="text-[13px] text-slate-500 leading-relaxed mb-6">
                    <span className="font-semibold text-slate-700">{docName}</span> will be permanently deleted and cannot be recovered.
                </p>
                <div className="flex justify-center gap-3">
                    <Btn variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Btn>
                    <Btn variant="danger" onClick={onConfirm} loading={busy}>Delete</Btn>
                </div>
            </div>
        </Modal>
    );
}


function ImagePreview({ doc, onClose }) {
    return (
        <Modal title={doc.original_name}
            sub={`${getProofCfg(doc.proof_type).label} · Doc #${doc.doc_index} · ${formatBytes(doc.size)} · ${doc.uploaded_at}`}
            onClose={onClose} width="max-w-3xl">
            <div className="rounded-xl overflow-hidden bg-slate-900 flex items-center justify-center min-h-[320px]">
                <img src={doc.file_url} alt={doc.original_name}
                    className="max-w-full max-h-[70vh] object-contain"
                    onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <div className="flex justify-end mt-4">
                <a href={doc.file_url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg text-[13px] font-semibold transition-colors no-underline">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                    Open in New Tab
                </a>
            </div>
        </Modal>
    );
}

function CreateModal({ vendorProfileId, onClose, onCreated }) {
    const [proofType, setProofType] = useState("itr");
    const [docIndex, setDocIndex] = useState("1");
    const [financialYear, setFinancialYear] = useState(FY_OPTIONS[0].value);
    const [figureAmount, setFigureAmount] = useState("");
    const [file, setFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState({});
    const fileRef = useRef(null);

    const handleFile = (f) => {
        if (!f) return;
        if (f.size > 10 * 1024 * 1024) { setErrors((p) => ({ ...p, file: "File must be under 10 MB." })); return; }
        setFile(f);
        setErrors((p) => { const n = { ...p }; delete n.file; return n; });
    };

    const validate = () => {
        const e = {};
        if (!proofType) e.proofType = "Select a proof type.";
        if (!docIndex) e.docIndex = "Select a document index.";
        if (!financialYear) e.financialYear = "Select a financial year.";
        if (!figureAmount || isNaN(parseFloat(figureAmount))) e.figureAmount = "Enter a valid amount.";
        if (!file) e.file = "Please select a file.";
        return e;
    };

    const handleSubmit = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setBusy(true);
        const tid = toast.loading("Uploading income document…");
        try {
            const fd = new FormData();
            fd.append("proof_type", proofType);
            fd.append("doc_index", docIndex);
            fd.append("financial_year", financialYear);
            fd.append("figure_amount", figureAmount);
            fd.append("file", file);

            const { data } = await incomeApi.create(vendorProfileId, fd);
            toast.dismiss(tid);

            if (data?.status >= 400) {
                const fe = {};
                if (data?.data?.file) fe.file = Array.isArray(data.data.file) ? data.data.file[0] : data.data.file;
                if (data?.data?.doc_index) fe.docIndex = Array.isArray(data.data.doc_index) ? data.data.doc_index[0] : data.data.doc_index;
                if (data?.data?.proof_type) fe.proofType = Array.isArray(data.data.proof_type) ? data.data.proof_type[0] : data.data.proof_type;
                if (data?.data?.figure_amount) fe.figureAmount = Array.isArray(data.data.figure_amount) ? data.data.figure_amount[0] : data.data.figure_amount;
                if (data?.data?.detail || data?.data?.non_field_errors) {
                    fe._global = data.data.detail || (Array.isArray(data.data.non_field_errors) ? data.data.non_field_errors[0] : data.data.non_field_errors);
                }
                if (Object.keys(fe).length === 0) fe._global = `Server error ${data?.status}. Please try again.`;
                setErrors(fe);
                return;
            }

            toast.success("Document Uploaded", `${file.name} added successfully.`);
            onCreated(data?.data ?? data);
        } catch (err) {
            toast.dismiss(tid);
            toast.error(err?.name === "TypeError" ? "Network Error" : "Upload Failed",
                err?.name === "TypeError" ? "Cannot reach the server." : "Something went wrong. Please retry.");
        } finally {
            setBusy(false);
        }
    };


    return (
        <Modal title="Upload Income Document" sub="Add ITR or Work Order or balance sheet proof for this vendor" onClose={onClose}>
            {errors._global && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-[13px] text-red-700 flex items-center gap-2">
                    <span className="text-base">⚠</span> {errors._global}
                </div>
            )}

            <div className="mb-4">
                <Label required>Proof Type</Label>
                <div className="grid grid-cols-3 gap-2.5">
                    {PROOF_TYPES.map((pt) => (
                        <button key={pt.value} onClick={() => { setProofType(pt.value); setErrors((p) => { const n = { ...p }; delete n.proofType; return n; }); }}
                            className={`p-3 rounded-xl border-2 text-left transition-all duration-150 ${proofType === pt.value ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                            {/* <div className="text-xl mb-1">{pt.emoji}</div> */}
                            <div className={`text-[12px] font-bold ${proofType === pt.value ? "text-emerald-700" : "text-slate-600"}`}>{pt.label}</div>
                        </button>
                    ))}
                </div>
                <FieldError msg={errors.proofType} />
            </div>

            <div className="mb-4">
                <Label required hint="(1–3, must be unique per proof type)">Document Index</Label>
                <div className="flex gap-2">
                    {DOC_INDICES.map((i) => (
                        <button key={i} onClick={() => { setDocIndex(String(i)); setErrors((p) => { const n = { ...p }; delete n.docIndex; return n; }); }}
                            className={`flex-1 py-2.5 rounded-xl border-2 text-[14px] font-bold transition-all duration-150 ${docIndex === String(i) ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>
                            #{i}
                        </button>
                    ))}
                </div>
                <FieldError msg={errors.docIndex} />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                    <Label required>Financial Year</Label>
                    <select value={financialYear} onChange={(e) => { setFinancialYear(e.target.value); setErrors((p) => { const n = { ...p }; delete n.financialYear; return n; }); }}
                        className={`w-full px-3 py-2 rounded-lg border text-[13.5px] text-slate-800 bg-slate-50 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors ${errors.financialYear ? "border-red-400 bg-red-50" : "border-slate-200"}`}>
                        {FY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <FieldError msg={errors.financialYear} />
                </div>
                <div>
                    <Label required hint="in ₹">Figure Amount</Label>
                    <input type="number" min="0" step="0.01" value={figureAmount}
                        onChange={(e) => { setFigureAmount(e.target.value); setErrors((p) => { const n = { ...p }; delete n.figureAmount; return n; }); }}
                        placeholder="e.g. 2000000"
                        className={`w-full px-3 py-2 rounded-lg border text-[13.5px] text-slate-800 bg-slate-50 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors ${errors.figureAmount ? "border-red-400 bg-red-50" : "border-slate-200"}`} />
                    {figureAmount && !isNaN(parseFloat(figureAmount)) && (
                        <p className="mt-1 text-[11px] text-emerald-600 font-semibold">{formatINR(figureAmount)}</p>
                    )}
                    <FieldError msg={errors.figureAmount} />
                </div>
            </div>

            <div className="mb-5">
                <Label required hint="PDF, PNG, JPG — max 10 MB">File</Label>
                <div
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
                    className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all duration-150 ${errors.file ? "border-red-300 bg-red-50" :
                            dragOver ? "border-emerald-400 bg-emerald-50" :
                                file ? "border-emerald-300 bg-emerald-50" :
                                    "border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white"
                        }`}>
                    <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
                        onChange={(e) => handleFile(e.target.files[0])} />
                    {file ? (
                        <div className="flex flex-col items-center gap-1.5">
                            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-xl">
                                {file.type.startsWith("image/") ? "🖼️" : "📄"}
                            </div>
                            <span className="text-[13.5px] font-bold text-slate-800">{file.name}</span>
                            <span className="text-[12px] text-slate-500">{formatBytes(file.size)}</span>
                            <span className="text-[11.5px] text-emerald-600 font-semibold">Click to replace</span>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-1.5">
                            <svg className="w-8 h-8 text-slate-300 mb-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                            <span className="text-[13.5px] font-semibold text-slate-600">Drop file or click to browse</span>
                            <span className="text-[12px] text-slate-400">PNG, JPG, PDF up to 10 MB</span>
                        </div>
                    )}
                </div>
                <FieldError msg={errors.file} />
            </div>

            <div className="flex justify-end gap-2.5">
                <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
                <Btn variant="primary" onClick={handleSubmit} loading={busy}
                    icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>}>
                    Upload Document
                </Btn>
            </div>
        </Modal>
    );
}


function EditModal({ vendorProfileId, doc, onClose, onUpdated }) {
    const initYears = () =>
        doc.doc_years?.length
            ? doc.doc_years.map((y) => ({ financial_year: y.financial_year, figure_amount: String(y.figure_amount) }))
            : [{ financial_year: doc.financial_year || FY_OPTIONS[0].value, figure_amount: String(doc.figure_amount || "") }];

    const [figureAmount, setFigureAmount] = useState(String(doc.figure_amount || ""));
    const [docYears, setDocYears] = useState(initYears);
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState({});

    const updateYear = (idx, field, val) => {
        setDocYears((prev) => prev.map((y, i) => i === idx ? { ...y, [field]: val } : y));
        setErrors((p) => { const n = { ...p }; delete n[`year_${idx}_${field}`]; return n; });
    };

    const validate = () => {
        const e = {};
        if (!figureAmount || isNaN(parseFloat(figureAmount))) e.figureAmount = "Enter a valid amount.";
        docYears.forEach((y, i) => {
            if (!y.financial_year) e[`year_${i}_financial_year`] = "Required";
            if (!y.figure_amount || isNaN(parseFloat(y.figure_amount))) e[`year_${i}_figure_amount`] = "Enter valid amount.";
        });
        return e;
    };

    const handleSubmit = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setBusy(true);
        const tid = toast.loading("Updating document…");
        try {
            const payload = {
                figure_amount: figureAmount,
                doc_years_payload: docYears.map((y) => ({ financial_year: y.financial_year, figure_amount: y.figure_amount })),
            };
            const { data } = await incomeApi.patch(vendorProfileId, doc.id, payload);
            toast.dismiss(tid);

            if (data?.status >= 400) {
                const fe = {};
                if (data?.data?.figure_amount) fe.figureAmount = Array.isArray(data.data.figure_amount) ? data.data.figure_amount[0] : data.data.figure_amount;
                if (data?.data?.doc_years_payload) fe._global = Array.isArray(data.data.doc_years_payload) ? data.data.doc_years_payload[0] : data.data.doc_years_payload;
                if (data?.data?.detail) fe._global = data.data.detail;
                if (Object.keys(fe).length === 0) fe._global = `Server error ${data?.status}.`;
                setErrors(fe);
                return;
            }

            toast.success("Document Updated", "Financial details saved successfully.");
            onUpdated(data?.data ?? data);
        } catch (err) {
            toast.dismiss(tid);
            toast.error(err?.name === "TypeError" ? "Network Error" : "Update Failed",
                err?.name === "TypeError" ? "Cannot reach the server." : "Something went wrong.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal title="Edit Financial Details" sub={`${doc.original_name} · Doc #${doc.doc_index}`} onClose={onClose}>
            {errors._global && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-[13px] text-red-700">⚠ {errors._global}</div>
            )}

            <div className="mb-5">
                <Label required hint="Overall figure (₹)">Figure Amount</Label>
                <input type="number" min="0" step="0.01" value={figureAmount}
                    onChange={(e) => { setFigureAmount(e.target.value); setErrors((p) => ({ ...p, figureAmount: undefined })); }}
                    className={`w-full px-3 py-2.5 rounded-lg border text-[13.5px] text-slate-800 bg-slate-50 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors ${errors.figureAmount ? "border-red-400 bg-red-50" : "border-slate-200"}`} />
                {figureAmount && !isNaN(parseFloat(figureAmount)) && (
                    <p className="mt-1 text-[11.5px] font-semibold text-emerald-600">{formatINR(figureAmount)}</p>
                )}
                <FieldError msg={errors.figureAmount} />
            </div>

            <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                    <Label>Year-wise Breakdown</Label>
                    <button onClick={() => setDocYears((p) => [...p, { financial_year: FY_OPTIONS[0].value, figure_amount: "" }])}
                        className="text-[12px] text-emerald-600 font-semibold hover:text-emerald-700 flex items-center gap-1 transition-colors">
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        Add Year
                    </button>
                </div>
                <div className="space-y-2.5">
                    {docYears.map((y, idx) => (
                        <div key={idx} className="flex gap-2.5 items-start p-3 bg-slate-50 rounded-xl border border-slate-100">
                            <div className="flex-1">
                                <p className="text-[11px] font-semibold text-slate-400 mb-1">Financial Year</p>
                                <select value={y.financial_year}
                                    onChange={(e) => updateYear(idx, "financial_year", e.target.value)}
                                    className={`w-full px-2.5 py-1.5 rounded-lg border text-[13px] text-slate-800 bg-white focus:outline-none focus:border-emerald-500 transition-colors ${errors[`year_${idx}_financial_year`] ? "border-red-400" : "border-slate-200"}`}>
                                    {FY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                                <FieldError msg={errors[`year_${idx}_financial_year`]} />
                            </div>
                            <div className="flex-1">
                                <p className="text-[11px] font-semibold text-slate-400 mb-1">Amount (₹)</p>
                                <input type="number" min="0" step="0.01" value={y.figure_amount}
                                    onChange={(e) => updateYear(idx, "figure_amount", e.target.value)}
                                    placeholder="Amount"
                                    className={`w-full px-2.5 py-1.5 rounded-lg border text-[13px] text-slate-800 bg-white focus:outline-none focus:border-emerald-500 transition-colors ${errors[`year_${idx}_figure_amount`] ? "border-red-400" : "border-slate-200"}`} />
                                {y.figure_amount && !isNaN(parseFloat(y.figure_amount)) && (
                                    <p className="mt-0.5 text-[10.5px] text-emerald-600 font-semibold">{formatINR(y.figure_amount)}</p>
                                )}
                                <FieldError msg={errors[`year_${idx}_figure_amount`]} />
                            </div>
                            {docYears.length > 1 && (
                                <button onClick={() => setDocYears((p) => p.filter((_, i) => i !== idx))}
                                    className="mt-5 p-1.5 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors">
                                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex justify-end gap-2.5">
                <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
                <Btn variant="primary" onClick={handleSubmit} loading={busy}
                    icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>}>
                    Save Changes
                </Btn>
            </div>
        </Modal>
    );
}


function IncomeDocCard({ doc, onEdit, onDelete, onPreview }) {
    const cfg = getProofCfg(doc.proof_type);
    const isImg = doc.content_type?.startsWith("image/");
    const [imgErr, setImgErr] = useState(false);
    const [hovering, setHovering] = useState(false);

    const totalAmount = doc.doc_years?.reduce((s, y) => s + parseFloat(y.figure_amount || 0), 0) || parseFloat(doc.figure_amount || 0);

    return (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
            onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>

            <div className={`h-36 relative overflow-hidden ${isImg && !imgErr ? "bg-slate-900" : "bg-gradient-to-br from-slate-50 to-slate-100"}`}>
                {isImg && !imgErr ? (
                    <>
                        <img src={doc.file_url} alt={doc.original_name}
                            className={`w-full h-full object-cover transition-transform duration-500 ${hovering ? "scale-105 opacity-80" : "opacity-90"}`}
                            onError={() => setImgErr(true)} />
                        <button onClick={() => onPreview(doc)}
                            className={`absolute inset-0 flex items-center justify-center transition-all duration-200 ${hovering ? "opacity-100" : "opacity-0"}`}>
                            <span className="flex items-center gap-1.5 bg-black/60 text-white text-[12px] font-semibold px-3 py-1.5 rounded-lg backdrop-blur-sm">
                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                Preview
                            </span>
                        </button>
                    </>
                ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-1.5">
                        <span className={`text-3xl ${cfg.tw.icon} p-3 rounded-xl`}>{cfg.emoji}</span>
                        <span className="text-[11px] text-slate-400 font-semibold">
                            {doc.content_type?.includes("pdf") ? "PDF Document" : "No Preview"}
                        </span>
                    </div>
                )}

                <div className="absolute top-2 left-2 flex gap-1.5">
                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ring-1 ${cfg.tw.pill}`}>
                        {cfg.emoji} {cfg.label}
                    </span>
                </div>
                <div className="absolute top-2 right-2">
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/90 text-slate-600 ring-1 ring-slate-200 backdrop-blur-sm">
                        #{doc.doc_index}
                    </span>
                </div>
            </div>

            <div className="p-4 flex flex-col gap-3 flex-1">
                <div>
                    <p className="text-[13.5px] font-bold text-slate-900 truncate" title={doc.original_name}>{doc.original_name}</p>
                    <p className="text-[12px] text-slate-400 mt-0.5">{formatBytes(doc.size)} · {doc.uploaded_at}</p>
                </div>

                <div className="bg-emerald-50 rounded-xl p-2.5 border border-emerald-100">
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10.5px] font-bold text-emerald-600 uppercase tracking-wide">Total Amount</span>
                        <span className="text-[13px] font-black text-emerald-700">{formatINR(totalAmount)}</span>
                    </div>
                    {doc.doc_years?.length > 0 && (
                        <div className="space-y-1">
                            {doc.doc_years.map((y) => (
                                <div key={y.id} className="flex items-center justify-between">
                                    <span className="text-[11px] text-emerald-600 font-medium">FY {y.financial_year}</span>
                                    <span className="text-[11px] font-bold text-emerald-700">{formatINR(y.figure_amount)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex gap-2 mt-auto">
                    <a href={doc.file_url} target="_blank" rel="noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-[12.5px] font-semibold text-blue-700 transition-colors no-underline">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                        Download
                    </a>
                    <button onClick={() => onEdit(doc)}
                        className="p-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-slate-500 hover:text-slate-700 transition-colors" title="Edit financial details">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                    </button>
                    <button onClick={() => onDelete(doc)}
                        className="p-1.5 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg text-red-400 hover:text-red-600 transition-colors" title="Delete document">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>
                    </button>
                </div>
            </div>
        </div>
    );
}


function StatsBar({ docs }) {
    const totalAmount = docs.reduce((s, d) => s + parseFloat(d.figure_amount || 0), 0);
    const itrCount = docs.filter((d) => d.proof_type === "itr").length;
    const woCount = docs.filter((d) => d.proof_type === "work_order").length;
    const balanceCount=docs.filter((d) => d.proof_type === "balance_sheet").length;
    return (
        <div className="grid grid-cols-3 gap-3 mb-5">
            {[
                { label: "Total Documents", value: docs.length, color: "text-slate-700", bg: "bg-slate-50 border-slate-200", sub: `${itrCount} ITR · ${woCount} Work Order ${balanceCount}. Audit Balance sheet` },
                { label: "Total Turnover", value: formatINR(totalAmount), color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", sub: "Sum of all income docs" },
                { label: "Document Slots", value: `${docs.length} / 3`, color: "text-blue-700", bg: "bg-blue-50 border-blue-200", sub: "Max 3 per proof type" },
            ].map(({ label, value, color, bg, sub }) => (
                <div key={label} className={`${bg} border rounded-2xl p-3.5`}>
                    <p className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
                    <p className={`text-[15px] font-black ${color} leading-tight`}>{value}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>
                </div>
            ))}
        </div>
    );
}


const VendorIncomeDocs = ({ vendorProfileId }) => {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [search, setSearch] = useState("");

    const [showCreate, setShowCreate] = useState(false);
    const [editDoc, setEditDoc] = useState(null);
    const [deleteDoc, setDeleteDoc] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const fetchDocs = useCallback(async (q = "") => {
        if (!vendorProfileId) { setFetchError("No vendor ID provided."); return; }
        setLoading(true);
        setFetchError(null);
        try {
            const { data } = await incomeApi.list(vendorProfileId, q);
            if (data?.status === 401) { setFetchError("Session expired. Please log in again."); return; }
            if (data?.status === 403) { setFetchError("You don't have permission to view these documents."); return; }
            if (data?.status >= 400) throw new Error("Load failed");
            if (data && (data?.status === "success" || Array.isArray(data?.results))) {
                setDocs(Array.isArray(data?.results) ? data?.results : []);
            } else if (Array.isArray(data)) {
                setDocs(data);
            } else {
                setFetchError("Unexpected response format from server.");
            }
        } catch (err) {
            const msg = err?.name === "TypeError"
                ? "Cannot reach the server. Check your connection."
                : `Failed to load income documents (${err.message}).`;
            setFetchError(msg);
            toast.error("Load Failed", msg);
        } finally {
            setLoading(false);
        }
    }, [vendorProfileId]);

    useEffect(() => { fetchDocs(""); }, [fetchDocs]);

    useEffect(() => {
        if (search === "") return;
        const t = setTimeout(() => fetchDocs(search), 400);
        return () => clearTimeout(t);
    }, [search, fetchDocs]);

    const handleRefresh = useCallback(() => fetchDocs(search), [fetchDocs, search]);

    const handleCreated = (saved) => {
        setDocs((p) => [saved, ...p]);
        setShowCreate(false);
        toast.success("Document Added", `${saved.original_name} uploaded successfully.`);
    };

    const handleUpdated = (saved) => {
        setDocs((p) => p.map((d) => (d.id === saved.id ? saved : d)));
        setEditDoc(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteDoc) return;
        setDeleteBusy(true);
        const tid = toast.loading("Deleting document…");
        try {
            const { data } = await incomeApi.delete(vendorProfileId, deleteDoc.id);
            toast.dismiss(tid);
            if (data?.status === 204 || data?.status === 200 || data?.status === 404) {
                setDocs((p) => p.filter((d) => d.id !== deleteDoc.id));
                toast.success("Deleted", `${deleteDoc.original_name} removed.`);
                setDeleteDoc(null);
            } else if (data?.status === 401) {
                toast.error("Unauthorized", "Session expired. Please log in again.");
            } else if (data?.status === 403) {
                toast.error("Forbidden", "You don't have permission to delete this.");
            } else if (data?.status >= 400) {
                toast.error("Delete Failed", data?.data?.detail || `Server error ${data?.status}.`);
            }
        } catch (err) {
            toast.dismiss(tid);
            toast.error(err?.name === "TypeError" ? "Network Error" : "Unexpected Error",
                err?.name === "TypeError" ? "Cannot reach the server." : "Something went wrong. Please retry.");
        } finally {
            setDeleteBusy(false);
        }
    };

    return (
        <>
            <style>{`
        @keyframes slideIn  { from{opacity:0;transform:translateX(20px) scale(.97)} to{opacity:1;transform:none} }
        @keyframes slideOut { from{opacity:1} to{opacity:0;transform:translateX(24px) scale(.95)} }
        @keyframes fadeUp   { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
      `}</style>

            <ToastContainer />

            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div className="flex items-center gap-2.5">
                  
                    <div>
                        <h3 className="text-[15px] font-bold text-slate-900 leading-tight">Income Documents</h3>
                        {/* <p className="text-[12px] text-slate-400">ITR & Work Order proof</p> */}
                    </div>
                    {!loading && (
                        <span className="px-2.5 py-0.5 rounded-full text-[11.5px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
                            {docs.length}
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    <Btn variant="secondary" onClick={handleRefresh} loading={loading}
                        icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>}>
                        Refresh
                    </Btn>
                    <Btn variant="primary" onClick={() => setShowCreate(true)}
                        icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>}>
                        Upload Document
                    </Btn>
                </div>
            </div>

            {!loading && !fetchError && docs.length > 0 && <StatsBar docs={docs} />}

            <div className="relative mb-5">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search income documents…"
                    className="w-full pl-9 pr-10 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-[13.5px] text-slate-800 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors" />
                {search && (
                    <button onClick={() => { setSearch(""); fetchDocs(""); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                )}
            </div>

            {fetchError && !loading && (
                <div className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-xl text-[13px] text-red-700 flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2"><span className="text-base">⚠</span>{fetchError}</span>
                    <Btn variant="secondary" onClick={handleRefresh} size="sm">Retry</Btn>
                </div>
            )}

            {loading && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => <SkeletonCard key={i} />)}
                </div>
            )}

            {!loading && !fetchError && docs.length === 0 && (
                <div className="flex flex-col items-center py-16 px-6 text-center bg-white rounded-2xl border-2 border-dashed border-slate-200">
                    <p className="text-[16px] font-bold text-slate-800 mb-2">
                        {search ? "No Documents Match" : "No Income Documents Yet"}
                    </p>
                    <p className="text-[13px] text-slate-400 max-w-xs leading-relaxed mb-5">
                        {search
                            ? `No income documents found for "${search}". Try a different term.`
                            : "Upload ITR returns or Work Order documents to prove vendor income."}
                    </p>
                    {search
                        ? <Btn variant="secondary" onClick={() => { setSearch(""); fetchDocs(""); }}
                            icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>}>
                            Clear Search
                        </Btn>
                        : <Btn variant="primary" onClick={() => setShowCreate(true)}
                            icon={<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>}>
                            Upload First Document
                        </Btn>}
                </div>
            )}

            {!loading && !fetchError && docs.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-[fadeUp_.2s_ease]">
                    {docs.map((doc) => (
                        <IncomeDocCard key={doc.id} doc={doc}
                            onEdit={(d) => setEditDoc(d)}
                            onDelete={(d) => setDeleteDoc(d)}
                            onPreview={(d) => setPreviewDoc(d)} />
                    ))}
                </div>
            )}

            {showCreate && (
                <CreateModal vendorProfileId={vendorProfileId} onClose={() => setShowCreate(false)} onCreated={handleCreated} />
            )}
            {editDoc && (
                <EditModal vendorProfileId={vendorProfileId} doc={editDoc} onClose={() => setEditDoc(null)} onUpdated={handleUpdated} />
            )}
            {deleteDoc && (
                <ConfirmDelete docName={deleteDoc.original_name} onConfirm={handleDeleteConfirm} onCancel={() => setDeleteDoc(null)} busy={deleteBusy} />
            )}
            {previewDoc && (
                <ImagePreview doc={previewDoc} onClose={() => setPreviewDoc(null)} />
            )}
        </>
    );
};

export default VendorIncomeDocs;